import { apiRoutes } from '@/constants/api-routes';
import { apiRequest } from './http';

/**
 * Submit-score payload. Mirrors the server's `SubmitScoreDto`.
 *
 *   - `readerId`: stringified bigint, scoped to the authenticated account.
 *   - `bookId`: UUID of the book the run was played against.
 *   - `score`: canonical metric used for leaderboard ranking.
 *   - `gameSlug`: optional discriminator; defaults to `cosmic_typer` server-side.
 *   - `stats`: opaque metric bag (wpm, accuracy, level…) the leaderboard
 *     can render alongside the score later.
 */
export interface SubmitGameScorePayload {
  readerId: string;
  bookId: string;
  score: number;
  gameSlug?: string;
  stats?: Record<string, unknown>;
}

export interface SubmitGameScoreResult {
  readerId: string;
  bookId: string;
  gameSlug: string;
  bestScore: number;
  playCount: number;
  /** True when the submitted score replaced the previous best. */
  improved: boolean;
  lastPlayedAt: string;
}

export interface LeaderboardEntry {
  readerId: string;
  readerName: string;
  readerAvatarSlug: string;
  bestScore: number;
  playCount: number;
  bestStats: Record<string, unknown>;
  lastPlayedAt: string;
  rank: number;
}

export interface LeaderboardQuery {
  bookId: string;
  gameSlug?: string;
  limit?: number;
}

/**
 * Compact summary used by the Book Detail "Global Leaderboard" widget.
 * Both fields can be null:
 *   - `top === null`: nobody has played this (book, game) yet.
 *   - `reader === null`: either no readerId was passed, or the reader
 *     hasn't played this (book, game).
 *
 * When the reader IS #1 both fields point at the same entry by value —
 * the widget renders the dedupe state.
 */
export interface LeaderboardSummary {
  top: LeaderboardEntry | null;
  reader: LeaderboardEntry | null;
}

export interface LeaderboardSummaryQuery {
  bookId: string;
  gameSlug?: string;
  readerId?: string;
}

export const gamesService = {
  /**
   * Records the result of a single game run. Server upserts on
   * (reader, book, game) — repeat submissions update `play_count` and
   * `last_played_at`, and only replace `best_score` / `best_stats` when
   * the new score is a strict improvement.
   *
   * The returned `improved` flag is what the client uses to gate the
   * "new high score!" UI without a second round-trip.
   */
  submit(payload: SubmitGameScorePayload): Promise<SubmitGameScoreResult> {
    return apiRequest<SubmitGameScoreResult>(apiRoutes.games.submitScore, {
      method: 'POST',
      body: payload,
    });
  },

  /**
   * Top-N leaderboard for a (book, game). Cross-account by design — this
   * is the global per-book leaderboard.
   */
  leaderboard(query: LeaderboardQuery): Promise<LeaderboardEntry[]> {
    return apiRequest<LeaderboardEntry[]>(apiRoutes.games.leaderboard, {
      query: {
        bookId: query.bookId,
        gameSlug: query.gameSlug,
        limit: query.limit,
      },
    });
  },

  /**
   * #1 + (optional) reader's own row in a single round-trip. Used by the
   * "Global Leaderboard" widget on the Book Detail modal to compare a
   * reader's best to the global top score.
   */
  summary(query: LeaderboardSummaryQuery): Promise<LeaderboardSummary> {
    return apiRequest<LeaderboardSummary>(apiRoutes.games.leaderboardSummary, {
      query: {
        bookId: query.bookId,
        gameSlug: query.gameSlug,
        readerId: query.readerId,
      },
    });
  },
};
